import {Controller} from "stimulus";
import {Liquid} from "liquidjs";

export default class PromptPreviewController extends Controller {
  static values = {
    template: String,
  };

  static targets = ["variableInput", "modalContent"];

  declare templateValue: string;
  declare variableInputTargets: HTMLInputElement[];
  declare modalContentTarget: HTMLElement;

  async open() {
    this.modalContentTarget.innerHTML = await this.renderTemplate();

    document.dispatchEvent(
      new CustomEvent("tlw-modal:trigger-open", {
        detail: {modal: "prompt-preview-modal"},
        bubbles: true,
      }),
    );
  }

  private async renderTemplate(): Promise<string> {
    const variables = this.collectVariables();
    const engine = new Liquid();

    // Give each variable a placeholder that survives HTML-escaping as a
    // recognisable marker — LiquidJS does not HTML-escape output by default,
    // so the placeholder arrives in the rendered string verbatim, and after
    // escapeHtml it becomes e.g. &lt;VARPREVIEW:name&gt; which we can
    // reliably swap for the highlighted span.
    const placeholders: Record<string, string> = {};
    for (const name of Object.keys(variables)) {
      placeholders[name] = `<VARPREVIEW:${name}>`;
    }

    let rendered: string;
    try {
      rendered = await engine.parseAndRender(this.templateValue, placeholders);
    } catch {
      rendered = this.templateValue;
    }

    let html = this.escapeHtml(rendered);

    for (const [name, value] of Object.entries(variables)) {
      const marker = this.escapeHtml(`<VARPREVIEW:${name}>`);
      const highlighted = `<span class="bg-lime-100 text-emerald">${this.escapeHtml(value)}</span>`;
      html = html.replaceAll(marker, highlighted);
    }

    return `<pre class="whitespace-pre-wrap font-mono text-sm leading-relaxed">${html}</pre>`;
  }

  private collectVariables(): Record<string, string> {
    const variables: Record<string, string> = {};
    for (const input of this.variableInputTargets) {
      const name = input.dataset.variableName;
      if (name) variables[name] = input.value;
    }
    return variables;
  }

  // Needed because we write into innerHTML — prompt content can contain raw < > &
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
}
